import { useMutation } from '@tanstack/react-query';

import { axiosInt, API_CORE_URL } from '../../..';
import { downloadBlob } from '../../../utils/download';
import { useMessage } from '../../../hooks/useMessage';
import type { SupportedLanguage } from '../../../translate';
import type { EpiExportFormat } from '../types/types';

export const EXPORT_EPI_KEY = 'exportEpi';

interface IExportRequest {
  epiId: string;
  format: EpiExportFormat;
  language: SupportedLanguage;
  /** Used only when the response carries no filename of its own. */
  fallbackName: string;
}

/**
 * Pulls the filename out of `Content-Disposition`.
 *
 * The BE sends both forms — `filename="Mounjaro-sr.docx"` and
 * `filename*=UTF-8''...` — so that a non-ASCII product name survives every
 * browser. The encoded one wins where present; it is the one that is correct
 * when the two disagree.
 */
const filenameFrom = (disposition: string | undefined): string | undefined => {
  if (!disposition) return undefined;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // Malformed percent-encoding: fall through to the plain form.
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1]?.trim();
};

/**
 * An error body arrives as a Blob like any other response when `responseType`
 * is `blob`, so the server's message has to be read back out of it.
 */
const messageFromBlob = async (data: unknown): Promise<string | undefined> => {
  if (!(data instanceof Blob)) return undefined;
  try {
    const parsed = JSON.parse(await data.text());
    return parsed?.message || parsed?.detail || parsed?.error;
  } catch {
    return undefined;
  }
};

const exportEpiApi = async ({
  epiId,
  format,
  language,
  fallbackName,
}: IExportRequest): Promise<void> => {
  const response = await axiosInt.get(
    `${API_CORE_URL}/epi/${epiId}/export/${format}`,
    { params: { language }, responseType: 'blob' }
  );
  const name =
    filenameFrom(response.headers?.['content-disposition']) ??
    `${fallbackName}-${language}.${format}`;
  downloadBlob(response.data, name);
};

/**
 * Exports an ePI as a printable document and saves it to disk.
 *
 * The document is rendered server-side — it is a QRD layout the editor does
 * not model — so this only asks for it and hands the response to the browser.
 * One language per document, with no fallback between them: the language asked
 * for is the one rendered, empty sections included.
 */
export const useExportEpi = () => {
  const message = useMessage();

  const { mutate: exportEpi, isPending: isExporting } = useMutation<
    void,
    unknown,
    IExportRequest
  >({
    mutationKey: [EXPORT_EPI_KEY],
    mutationFn: exportEpiApi,
    onError: async error => {
      const server = await messageFromBlob(
        (error as { response?: { data?: unknown } })?.response?.data
      );
      message.error(
        server ?? 'Failed to export the document. Please try again.'
      );
    },
  });

  return { exportEpi, isExporting };
};
